import { useContext, useEffect, useMemo } from 'react'
import { useSearchEstablishmentMarkers } from '@/modules/accredited-establishments/hooks'
import {
  defineZoom,
  makeGetEstablishmentsArgs,
  SearchFiltersProps
} from '@/modules/accredited-establishments/functions'
import {
  FiltersContext,
  FiltersDispatchContext
} from '@/modules/accredited-establishments/contexts'
import {
  CoordinatesPayload,
  AddressPayload
} from '@/modules/accredited-establishments/hooks/useFiltersReducer/useFiltersReducer.types'
import { useMap } from '@vis.gl/react-google-maps'
import { EventName } from '@/global/utils/analytics-events/types/common'
import { ALL_ID } from '@/modules/accredited-establishments/constants/filters'
import { dispatchEvent } from '@/global/utils'
import { ANALYTICS_EVENTS_CONSENT_CATEGORY } from '@/global/utils/analytics-events/constants'
import { useMediaQuery } from '@/global/hooks'

const useMarkers = () => {
  const isMobile = useMediaQuery({ maxWidth: 'mobile' })

  const {
    view,
    markerLocation,
    categories,
    plan,
    specialty,
    subSpecialty,
    gender,
    appointmentType,
    providerName,
    ready: areFiltersReady
  } = useContext(FiltersContext)

  const map = useMap()

  const filtersDispatch = useContext(FiltersDispatchContext)

  const filters: SearchFiltersProps = {
    ...markerLocation,
    categories,
    plan,
    specialty,
    subSpecialty,
    gender,
    appointmentType,
    providerName
  }

  const args = makeGetEstablishmentsArgs({
    searchFilters: filters,
    enabled: areFiltersReady && view === 'map'
  })

  const { data, isFetching, isFetched, isError, refetch } =
    useSearchEstablishmentMarkers({ ...args, toastOnError: false })

  const markers = useMemo(() => data?.items ?? [], [data])
  const searchPosition = useMemo(() => data?.searchPosition ?? null, [data])

  const hasResults = markers.length > 0

  useEffect(() => {
    if (!isFetched || !searchPosition || !map || !areFiltersReady) return

    const center = {
      lat: Number(searchPosition.lat),
      lng: Number(searchPosition.lon)
    }

    if (searchPosition.type) {
      map.setZoom(defineZoom(searchPosition.type))
      map.setCenter(center)
    }

    if (view === 'map' && !hasResults) {
      const radiusInMeters =
        markerLocation.type === 'coordinates'
          ? markerLocation.radiusInMeters
          : undefined

      const location =
        markerLocation.type === 'address'
          ? { address: markerLocation.address?.title }
          : { lat: markerLocation.lat, lon: markerLocation.lon }

      map.setCenter(center)

      dispatchEvent(
        {
          eventName: EventName.NOT_FOUND_ESTABLISHMENTS,
          categories: categories.length ? categories?.join(',') : undefined,
          plan: plan?.value === ALL_ID ? undefined : plan?.title,
          specialty: specialty?.value === ALL_ID ? undefined : specialty?.title,
          radiusInMeters: radiusInMeters,
          ...location
        },
        { consentCategory: ANALYTICS_EVENTS_CONSENT_CATEGORY }
      )
    }
  }, [searchPosition, map, isFetched, view])

  useEffect(() => {
    if (isMobile && isFetched && !hasResults) {
      if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur()
      }
    }
  }, [isMobile, isFetched, hasResults])

  const searchByLatLon = (location: CoordinatesPayload) => {
    filtersDispatch({ type: 'UPDATE_COORDINATES', payload: location })
  }

  const searchByAddress = (location: AddressPayload) => {
    filtersDispatch({ type: 'UPDATE_MARKER_LOCATION', payload: location })
  }

  const searchClusterItems = (location: CoordinatesPayload) => {
    filtersDispatch({ type: 'UPDATE_CLUSTER_LOCATION', payload: location })
  }

  return {
    view,
    markers,
    isFetching,
    isFetched,
    searchPosition,
    markerLocation,
    isError,
    hasResults,
    refetch,
    searchByLatLon,
    searchByAddress,
    searchClusterItems
  }
}

export { useMarkers }
